import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface LocationData {
  address: string | undefined;
  lat: number;
  lng: number;
}

interface LocationContextType {
  location: string;
  setLocation: (location: string) => void;
  userLocation: { lat: number; lon: number } | null;
  setUserLocation: (location: { lat: number; lon: number } | null) => void;
  handlePlaceSelect: (locationData: LocationData) => void;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export function LocationProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState("");
  const [userLocation, setUserLocation] = useState<{ lat: number; lon: number } | null>(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          setUserLocation({ lat, lon });

          // Reverse geocode to get the actual address
          try {
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lon}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();
            if (data.status === "OK" && data.results.length > 0) {
              setLocation(data.results[0].formatted_address);
            } else {
              // If no address is fetched from API, use browser's reverse geocoding if available (not natively available in browser)
              // Fallback: use coordinates as address string
              setLocation(`${lat}, ${lon}`);
            }
          } catch {
            // If error, use coordinates as address
            setLocation(`${lat}, ${lon}`);
          }
        },
        () => setLocation("")
      );
    }
  }, []);

  const handlePlaceSelect = (locationData: LocationData) => {
    setLocation(locationData.address || "");
    setUserLocation({ lat: locationData.lat, lon: locationData.lng });
  };

  return (
    <LocationContext.Provider
      value={{
        location,
        setLocation,
        userLocation,
        setUserLocation,
        handlePlaceSelect,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
} 